import CustomButton from "@components/buttons/CustomButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { institutionApis } from "@services/apis/Institution";
import { useMemo, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import { INSTITUTION_ACCOUNT_CATEGORIES } from "../utils/accountCategories";

interface AddInstitutionServiceSliderProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  existingAccountCategoryIds?: number[];
  onSuccess: () => void;
}

const AddInstitutionServiceSlider: React.FC<
  AddInstitutionServiceSliderProps
> = ({
  isOpen,
  onClose,
  userId,
  existingAccountCategoryIds = [],
  onSuccess,
}) => {
  const [accountCategoryId, setAccountCategoryId] = useState<
    number | undefined
  >(undefined);
  const [merchantName, setMerchantName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { handleSuccess, handleError } = useSnackbarMessage();

  const availableCategories = useMemo(
    () =>
      INSTITUTION_ACCOUNT_CATEGORIES.filter(
        (category) => !existingAccountCategoryIds.includes(category.id),
      ),
    [existingAccountCategoryIds],
  );

  const handleClose = () => {
    setAccountCategoryId(undefined);
    setMerchantName("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!accountCategoryId) return;

    try {
      setLoading(true);
      const response = await institutionApis.createInstitutionMerchant({
        userId,
        accountCategoryId,
        ...(merchantName.trim() ? { merchantName: merchantName.trim() } : {}),
      });
      handleSuccess({
        message: response.msg || "Institution merchant created successfully",
      });
      handleClose();
      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Failed to create institution merchant. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Add Service"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
      isSlider={true}
      widthDesktop="420px"
    >
      <div className="flex flex-col p-[16px] gap-[16px] mt-[56px]">
        <MerchantDropdown
          listOfValues={availableCategories.map((category) => ({
            id: category.id,
            name: `${category.name} (${category.gateway})`,
          }))}
          value={accountCategoryId}
          onChange={(value) => setAccountCategoryId(Number(value))}
          label="Select Account Category"
          color="bg-page-body"
          expandedBorderColor="border-button-primary"
          baseColor="bg-page-body"
          borderColor="border-subtle"
        />
        <GlobalTextInputField
          title="Merchant Name (Optional)"
          value={merchantName}
          onChange={(e) => setMerchantName(e.target.value)}
          placeholder="Enter merchant name"
        />
        {availableCategories.length === 0 && (
          <p className="text-text-label text-[12px]">
            All available services have already been added for this institution.
          </p>
        )}
        <CustomButton
          text="CREATE SERVICE"
          isActive={!!accountCategoryId}
          isButtonLoading={loading}
          onClick={handleSubmit}
        />
      </div>
    </SlidingPanel>
  );
};

export default AddInstitutionServiceSlider;
